import axios from 'axios'
import { defineStore } from 'pinia'
import type { IAuthStoreState } from '../types/IAuthStoreState'
import { usePushSubscription } from '@/composables/usePushSubscription'
import { clearMatrixChatsOnLogout } from '@/modules/chat-matrix-fast/services/matrixChatCleanup'
import { BASE_URL, LOGTO_APP_ID } from '@/environment/environment'
import { useCentrifugeStore } from '@/modules/centrifuge/stores/centrifugeStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { useUserStore } from '@/modules/user/stores/userStore'
import { logtoClient } from '@/plugins/logto'
import { beginAuthTransition, currentAuthEpoch, describeAuthFailure, isExpiredAuth, readAuthProfile, sessionSubject } from '@/utils/authRecovery'
import { describeStoredTokens, markTokenWipe, reloadAccessTokenFromStorage } from '@/utils/tokenTrace'
import router from '@/router'
import { ERouteNames } from '@/router/ERouteNames'
import { ECookiesNames } from '@/types/ECookiesNames'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'
import { deleteCookie, getCookie, setCookie } from '@/utils/cookies'
import { logger } from '@/utils/eventLogger'
import { clearAdminCookies } from '@/utils/adminCookies'
import { useCentrifugeHelper } from '@/utils/subscribeHelpers'

let expiringSession: Promise<void> | undefined
let idTokenSync: Promise<void> | undefined

if (typeof window !== 'undefined' && LOGTO_APP_ID) {
  const idTokenKey = `logto:${LOGTO_APP_ID}:idToken`
  window.addEventListener('storage', event => {
    if (event.key !== idTokenKey) return
    void useAuthStore().adoptSessionFromAnotherTab(event.oldValue, event.newValue)
  })
}

export const useAuthStore = defineStore('authStore', {
  state: (): IAuthStoreState => ({
    isAuthenticated: false,
    sessionError: null,
    centrifugeToken: getCookie(ECookiesNames.CENTRIFUGE_TOKEN) || null,
    centrifugeTokenExpiry: getCookie(ECookiesNames.CENTRIFUGE_TOKEN_EXPIRY)
      ? new Date(getCookie(ECookiesNames.CENTRIFUGE_TOKEN_EXPIRY) as string)
      : null,
    centrifugeUrl: null,
    isShowLogoutConfirm: false,
    isSyncing: false,
  }),

  actions: {
    /** Explicit user action: clear this portal and end the shared SSO session. */
    async logout(): Promise<void> {
      if (expiringSession) await expiringSession
      beginAuthTransition()
      logger.info('[Auth] Запуск процесса logout пользователя')
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        this.clearAuthData()
        clearAdminCookies()
        await this.clearMatrixDataOnLogout()

        if (logtoClient) {
          try {
            logger.info('[Auth] Вызов logtoClient.signOut для завершения сессии в Logto')
            markTokenWipe('logout')
            await logtoClient.signOut(`${window.location.origin}/login`)
          } catch (e) {
            logger.error('[Auth] Исключение при вызове logtoClient.signOut:', e)
            markTokenWipe('logout')
            await logtoClient.clearAllTokens()
            await router.push({ name: ERouteNames.LOGIN })
          }
        } else {
          logger.warn('[Auth] logtoClient отсутствует при выходе, переход на /login')
          await router.push({ name: ERouteNames.LOGIN })
        }
      } finally {
        feedbackStore.isGlobalLoading = false
        logger.info('[Auth] Завершение процесса logout')
      }
    },

    /** Expiry is local to this portal. Only explicit logout ends global SSO. */
    async expireSession(reason = 'причина не передана'): Promise<void> {
      if (expiringSession) return expiringSession
      markTokenWipe(reason.includes('invalid_grant') ? 'refresh-rejected' : 'session-ended')
      logger.error('[Токены] Сессию закрываем.', reason, describeStoredTokens())
      beginAuthTransition()
      this.clearAuthData()
      clearAdminCookies()
      expiringSession = (async () => {
        await logtoClient?.clearAllTokens()
        await this.clearMatrixDataOnLogout()
        if (router.currentRoute.value.path !== '/login') {
          await router.replace('/login')
        }
      })()
      try {
        await expiringSession
      } finally {
        expiringSession = undefined
      }
    },

    /** Validate IAM before opening the portal; retain SSO on temporary failures. */
    async restoreAuth(): Promise<boolean> {
      this.sessionError = null
      this.isAuthenticated = false
      const client = logtoClient
      if (!client) return false
      const epoch = currentAuthEpoch()
      try {
        if (!await client.isAuthenticated()) return false
        const response = await readAuthProfile(
          () => {
            if (epoch !== currentAuthEpoch()) throw new Error('Session changed')
            return useUserStore().getUserProfile({
              authRecovery: true, isCurrent: () => epoch === currentAuthEpoch(),
            })
          },
          () => {
            if (epoch !== currentAuthEpoch()) throw new Error('Session changed')
            return client.clearAccessToken()
          },
        )
        if (epoch !== currentAuthEpoch()) return false
        if (!response || response.status !== 200 || !response.data) {
          throw new Error('Invalid IAM response')
        }
        this.isAuthenticated = true
      } catch (error) {
        if (epoch !== currentAuthEpoch()) return false
        if (isExpiredAuth(error)) {
          await this.expireSession(describeAuthFailure(error))
          this.sessionError = 'Сессия портала истекла. Нажмите «Войти», чтобы продолжить.'
        } else {
          this.sessionError = axios.isAxiosError(error) && error.response?.status === 403
            ? 'Нет доступа к этому порталу. Обратитесь к администратору или смените аккаунт.'
            : 'Не удалось подключиться к порталу. Повторите попытку — вход в аккаунт сохранён.'
        }
        return false
      }

      // Realtime and push outages must not undo a successful IAM check.
      try {
        await this.refreshCentrifugeToken()
        if (epoch !== currentAuthEpoch()) return false
        await useCentrifugeStore().initCentrifuge()
        await useCentrifugeHelper().subscribeToUserChannel()
      } catch {
        logger.warn('[RestoreAuth] Подключение уведомлений временно недоступно')
      }
      if (epoch !== currentAuthEpoch()) return false
      void usePushSubscription().ensureSubscription().catch(() => {
        logger.warn('[RestoreAuth] Подписка на Web Push временно недоступна')
      })
      return this.isAuthenticated
    },

    /**
     * Очищает все локальные данные аутентификации пользователя
     *
     * Выполняет:
     * - Удаление Centrifuge-куков (наши, не Logto)
     * - Сброс флага isAuthenticated
     * - Обнуление Centrifuge-полей (token, expiry, url)
     * - Очистку userStore.user
     *
     * Особенности:
     * - Очистка AUTH_TOKEN/AUTH_TOKEN_EXPIRY удалена: этих cookie больше нет
     * - Очистка ключей logto:* из localStorage удалена: signOut() SDK делает это сам
     * - Используется в logout() и restoreAuth() при битой сессии (локальная очистка)
     */
    clearAuthData() {
      const userStore = useUserStore()

      // Close all subscriptions and the socket before clearing the identity.
      // The SDK must not reconnect a previous user's session after logout.
      try {
        useCentrifugeStore().disconnect()
      } catch (e) {
        console.warn('[Auth] Realtime disconnect failed:', e)
      }

      deleteCookie(ECookiesNames.CENTRIFUGE_TOKEN)
      deleteCookie(ECookiesNames.CENTRIFUGE_TOKEN_EXPIRY)
      deleteCookie(ECookiesNames.SESSION_ID)
      localStorage.removeItem('last_synced_id_token')
      this.isAuthenticated = false
      this.sessionError = null
      this.centrifugeToken = null
      this.centrifugeUrl = null
      this.centrifugeTokenExpiry = null
      userStore.user = null
      usePermissionsStore().clearPermissions()
      useOnboardingStore().$reset()
    },

    async clearMatrixDataOnLogout(): Promise<void> {
      await clearMatrixChatsOnLogout()
    },

    /**
     * Обновляет токен для подключения к Centrifugo
     *
     * Выполняет:
     * - Запрос нового токена и URL для Centrifugo через POST /api/centrifugo/refresh-token/
     * - Bearer к этому запросу подставляется axios-интерсептором из Logto SDK
     * - Обновление данных в хранилище (токен, URL, срок действия) и соответствующих cookies
     *
     * Особенности:
     * - Временные ошибки повторяет SDK через getToken, без блокировки интерфейса.
     * - Ответ предыдущей auth-сессии не меняет токены текущего пользователя.
     */
    async refreshCentrifugeToken({ throwOnError = false } = {}) {
      const epoch = currentAuthEpoch()
      try {
        const response = await axios.post(`${BASE_URL}/api/centrifugo/refresh-token/`)
        if (epoch !== currentAuthEpoch() || !this.isAuthenticated) return false
        const data = response.data
        const expiry = new Date(data?.expiry)
        if (!data?.token || !data?.url || !Number.isFinite(expiry.getTime()) || expiry.getTime() <= Date.now())
          throw new Error('Incomplete realtime credentials')

        this.centrifugeToken = data.token
        this.centrifugeTokenExpiry = expiry
        this.centrifugeUrl = data.url
        setCookie({ name: ECookiesNames.CENTRIFUGE_TOKEN, value: data.token, expires: expiry, path: '/' })
        setCookie({ name: ECookiesNames.CENTRIFUGE_TOKEN_EXPIRY, value: expiry.toISOString(), expires: expiry, path: '/' })
        return true
      } catch (error) {
        // Keep the portal session and endpoint during transient outages. The SDK
        // owns retries; refreshing realtime credentials must not block the UI.
        if (throwOnError) throw error
        logger.warn('[Centrifugo] Не удалось обновить токен подключения')
        return false
      }
    },

    /**
     * Пока POST /api/account/sync/ не ответил, повторный вызов ждёт тот же запрос.
     * Ошибка синхронизации не останавливает рабочие запросы.
     * После выхода отметка last_synced_id_token не записывается заново.
     */
    async checkAndSyncIdToken(): Promise<void> {
      if (!logtoClient || !LOGTO_APP_ID) return
      if (idTokenSync) return idTokenSync

      const prefix = `logto:${LOGTO_APP_ID}:`
      const idToken = localStorage.getItem(`${prefix}idToken`)

      if (!idToken) {
        logger.warn('[Auth] ID Token отсутствует в localStorage')
        return
      }

      const lastSyncedIdToken = localStorage.getItem('last_synced_id_token')

      if (idToken === lastSyncedIdToken) return

      const epoch = currentAuthEpoch()
      this.isSyncing = true
      logger.info('[Auth] Запуск синхронизации нового ID-токена с бэкендом...')
      idTokenSync = (async () => {
        try {
          await axios.post(
            `${BASE_URL}/api/account/sync/`,
            { idToken },
            {
              headers: {
                'Content-Type': 'application/json',
              },
              skipAuthLogout: true,
            } as ICustomAxiosRequestConfig,
          )
          if (epoch !== currentAuthEpoch()) return
          localStorage.setItem('last_synced_id_token', idToken)
          logger.info('[Auth] Синхронизация ID-токена успешно выполнена')
        } catch (error) {
          logger.error('[Auth] Ошибка синхронизации ID-токена с бэкендом:', describeAuthFailure(error))
        } finally {
          this.isSyncing = false
          idTokenSync = undefined
        }
      })()

      return idTokenSync
    },

    /**
     * Другая вкладка сменила id-токен в общем хранилище.
     * Эта вкладка событие записи не получает.
     */
    async adoptSessionFromAnotherTab(previousIdToken: string | null, nextIdToken: string | null): Promise<void> {
      const previousSubject = sessionSubject(previousIdToken)
      const nextSubject = sessionSubject(nextIdToken)
      if (previousSubject && nextSubject && previousSubject === nextSubject) {
        logger.warn('[Токены] Другая вкладка обновила id token того же пользователя. Access token не стираем, берём из хранилища тот, что записала другая вкладка.', describeStoredTokens())
        if (logtoClient)
          await reloadAccessTokenFromStorage(logtoClient)
        return
      }

      logger.warn('[Токены] Другая вкладка сменила пользователя или закрыла сессию. Эта вкладка сбрасывает свой вход.', describeStoredTokens())
      beginAuthTransition()
      await logtoClient?.clearAccessToken()
      this.clearAuthData()
      clearAdminCookies()
      if (!nextIdToken) {
        if (router.currentRoute.value.path !== '/login')
          await router.replace('/login')
        return
      }

      const restored = await this.restoreAuth()
      if (!restored && router.currentRoute.value.path !== '/login')
        await router.replace('/login')
    },
  },
})
